import React from "react";
import type { BatchCase, BatchCaseStatus } from "../../hooks/useBatchUpload";

const isTerminal = (s: BatchCaseStatus) => s === "ready" || s === "failed" || s === "upload-failed";

const badge = (c: BatchCase) => {
  if (c.status === "ready")
    return <span className="font-mono text-[10px] text-signal">READY ✓</span>;
  if (c.status === "failed" || c.status === "upload-failed")
    return <span className="font-mono text-[10px] text-warn">FAILED</span>;
  // A retry re-enters this panel as `uploading` before it's back in analyze —
  // don't badge an in-flight re-upload as ANALYZING.
  if (c.status === "uploading")
    return <span className="font-mono text-[10px] text-fg/40">UPLOADING…</span>;
  return <span className="font-mono text-[10px] text-fg/40">ANALYZING…</span>;
};

/** Per-case analyze status + a barrier progress line ("N of M complete"). */
export const BatchAnalyzePanel: React.FC<{ cases: BatchCase[] }> = ({ cases }) => {
  const done = cases.filter((c) => isTerminal(c.status)).length;
  return (
    <div className="flex flex-col gap-3">
      <div
        className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim"
        data-testid="batch-analyze-count"
      >
        Analyzing — {done} of {cases.length} complete
      </div>
      <div className="h-1 w-full overflow-hidden rounded bg-border">
        <div
          className="h-full bg-signal transition-all"
          style={{ width: `${cases.length ? (done / cases.length) * 100 : 0}%` }}
        />
      </div>
      <div className="max-h-72 overflow-y-auto rounded border border-border custom-scrollbar">
        {cases.map((c) => (
          <div
            key={c.studyInstanceUid}
            className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
          >
            <div className="min-w-0">
              <div className="truncate font-mono text-[12px] text-fg/80">
                …{c.studyInstanceUid.slice(-16)}
              </div>
              {c.message && <div className="truncate text-[11px] text-fg/40">{c.message}</div>}
            </div>
            {badge(c)}
          </div>
        ))}
      </div>
    </div>
  );
};
